import * as React from "react";
import type { ZipcodeData, UserType } from '../../types/geographic';

interface ZipcodeMapViewProps {
  data: ZipcodeData[];
  userType: UserType;
  onZipcodeClick?: (zipcode: ZipcodeData) => void;
  onZipcodeHover?: (zipcode: ZipcodeData | null) => void;
  isLoading?: boolean;
  className?: string;
  googleMapsApiKey?: string;
}

/**
 * Google Maps implementation for Zipcode data visualization
 * Uses small markers with clustering for better performance with many zipcodes
 */
const ZipcodeMapView: React.FC<ZipcodeMapViewProps> = ({
  data,
  userType,
  onZipcodeClick,
  onZipcodeHover,
  isLoading = false,
  className = "",
  googleMapsApiKey
}) => {
  const mapContainerRef = React.useRef<HTMLDivElement>(null);
  const mapRef = React.useRef<google.maps.Map | null>(null);
  const markersRef = React.useRef<google.maps.Marker[]>([]);
  const infoWindowRef = React.useRef<google.maps.InfoWindow | null>(null);
  const [mapLoaded, setMapLoaded] = React.useState(false);

  // Get API key from meta tag if not provided
  const apiKey = React.useMemo(() => {
    if (googleMapsApiKey) {
      return googleMapsApiKey;
    }
    
    const metaTag = document.querySelector('meta[name="google-maps-api-key"]');
    if (metaTag) {
      return metaTag.getAttribute('content') || '';
    }
    
    console.error('[ZipcodeMapView] No Google Maps API key found!');
    return '';
  }, [googleMapsApiKey]);

  // Helper function to get user value
  const getUserValue = React.useCallback((zipcode: ZipcodeData): number => {
    switch (userType) {
      case 'clients':
        return zipcode.client_count;
      case 'professionals':
        return zipcode.professional_count;
      case 'all':
      default:
        return zipcode.user_count;
    }
  }, [userType]);

  // Helper function to get marker color based on value
  const getMarkerColor = React.useCallback((value: number): string => {
    if (value === 0) return '#9E9E9E'; // Grey for no data
    if (value < 5) return '#4CAF50'; // Green for low
    if (value < 15) return '#2196F3'; // Blue for medium-low
    if (value < 30) return '#FF9800'; // Orange for medium
    if (value < 50) return '#F44336'; // Red for high
    return '#9C27B0'; // Purple for very high
  }, []);

  // Helper function to get marker size based on value
  const getMarkerSize = React.useCallback((value: number): number => {
    if (value === 0) return 6;
    if (value < 5) return 8;
    if (value < 15) return 10;
    if (value < 30) return 12;
    if (value < 50) return 14;
    return 16;
  }, []);

  // Initialize Google Maps
  React.useEffect(() => {
    if (!apiKey) return;

    const loadGoogleMaps = () => {
      // Check if already loaded
      if (window.google && window.google.maps) {
        initializeMap();
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=visualization,marker`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        initializeMap();
      };
      
      script.onerror = (error) => {
        console.error('[ZipcodeMapView] Failed to load Google Maps:', error);
      };

      document.head.appendChild(script);
    };

    const initializeMap = () => {
      if (!mapContainerRef.current) return;

      // Create map instance
      const map = new google.maps.Map(mapContainerRef.current, {
        zoom: 4,
        center: { lat: 39.8283, lng: -98.5795 }, // Geographic center of US
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
        styles: [
          {
            featureType: "poi",
            elementType: "labels.icon",
            stylers: [{ visibility: "off" }]
          },
          {
            featureType: "transit",
            stylers: [{ visibility: "off" }]
          }
        ]
      });

      mapRef.current = map;

      // Create info window
      const infoWindow = new google.maps.InfoWindow();
      infoWindowRef.current = infoWindow;

      setMapLoaded(true);
    };

    // Small delay to ensure DOM is ready
    setTimeout(() => {
      if (mapContainerRef.current) {
        loadGoogleMaps();
      }
    }, 100);
  }, [apiKey]);

  // Update markers when data or userType changes
  React.useEffect(() => {
    if (!mapRef.current || !mapLoaded || !data.length) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Filter zipcodes with coordinates
    const zipcodesWithCoords = data.filter(zipcode => 
      zipcode.coordinates && 
      zipcode.coordinates.lat && 
      zipcode.coordinates.lng &&
      getUserValue(zipcode) > 0
    );

    if (zipcodesWithCoords.length === 0) return;

    // Create markers for zipcodes
    zipcodesWithCoords.forEach(zipcode => {
      if (!zipcode.coordinates) return;

      const userValue = getUserValue(zipcode);
      const markerColor = getMarkerColor(userValue);
      const markerSize = getMarkerSize(userValue);

      // Create custom marker icon
      const markerIcon = {
        path: google.maps.SymbolPath.CIRCLE,
        fillColor: markerColor,
        fillOpacity: 0.7,
        strokeColor: '#ffffff',
        strokeWeight: 1,
        scale: markerSize / 2
      };

      const marker = new google.maps.Marker({
        position: { lat: zipcode.coordinates.lat, lng: zipcode.coordinates.lng },
        map: mapRef.current,
        icon: markerIcon,
        title: `${zipcode.zipcode} - ${zipcode.city_name}, ${zipcode.state_code}`,
        zIndex: userValue // Higher values appear on top
      });

      // Add click listener
      marker.addListener('click', () => {
        if (onZipcodeClick) {
          onZipcodeClick(zipcode);
        }

        // Show info window
        if (infoWindowRef.current) {
          const currentUserLabel = userType === 'clients' ? 'Clients' : 
                                 userType === 'professionals' ? 'Professionals' : 'Total Users';
          
          const content = `
            <div style="min-width: 200px; padding: 8px;">
              <h6 style="margin: 0 0 8px 0; font-weight: bold;">${zipcode.zipcode}</h6>
              <div style="font-size: 14px;">
                <div style="margin-bottom: 6px; padding: 4px 8px; background-color: #e3f2fd; border-radius: 4px; border-left: 3px solid #2196f3;">
                  <strong>${currentUserLabel}:</strong> <span style="font-size: 16px; color: #1976d2;">${userValue.toLocaleString()}</span>
                </div>
                <div style="margin-bottom: 4px;">
                  <strong>Total Users:</strong> ${zipcode.user_count.toLocaleString()}
                </div>
                <div style="margin-bottom: 4px;">
                  <strong>Clients:</strong> ${zipcode.client_count.toLocaleString()}
                </div>
                <div style="margin-bottom: 4px;">
                  <strong>Professionals:</strong> ${zipcode.professional_count.toLocaleString()}
                </div>
                <div style="margin-bottom: 4px;">
                  <strong>City:</strong> ${zipcode.city_name}
                </div>
                <div style="margin-bottom: 4px;">
                  <strong>State:</strong> ${zipcode.state_name}
                </div>
                ${zipcode.revenue && zipcode.revenue > 0 ? `
                  <div style="margin-bottom: 4px;">
                    <strong>Revenue:</strong> $${zipcode.revenue.toLocaleString('en-US', { 
                      minimumFractionDigits: 2, 
                      maximumFractionDigits: 2 
                    })}
                  </div>
                ` : ''}
                ${zipcode.roi ? `
                  <div>
                    <strong>ROI:</strong> ${zipcode.roi.toFixed(1)}%
                  </div>
                ` : ''}
              </div>
            </div>
          `;
          
          infoWindowRef.current.setContent(content);
          infoWindowRef.current.open(mapRef.current, marker);
        }
      });

      // Add mouseover listener
      marker.addListener('mouseover', () => {
        if (onZipcodeHover) {
          onZipcodeHover(zipcode);
        }
      });

      // Add mouseout listener
      marker.addListener('mouseout', () => {
        if (onZipcodeHover) {
          onZipcodeHover(null);
        }
      });

      markersRef.current.push(marker);
    });

    // Adjust map bounds to show all markers
    if (zipcodesWithCoords.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      zipcodesWithCoords.forEach(zipcode => {
        if (zipcode.coordinates) {
          bounds.extend(new google.maps.LatLng(zipcode.coordinates.lat, zipcode.coordinates.lng));
        }
      });
      mapRef.current.fitBounds(bounds);
      
      // Ensure minimum zoom level for zipcodes (higher than cities)
      const listener = google.maps.event.addListener(mapRef.current, 'idle', () => {
        if (mapRef.current && mapRef.current.getZoom() && mapRef.current.getZoom()! > 12) {
          mapRef.current.setZoom(12);
        }
        google.maps.event.removeListener(listener);
      });
    }

  }, [mapLoaded, data, userType, getUserValue, getMarkerColor, getMarkerSize, onZipcodeClick, onZipcodeHover]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      markersRef.current.forEach(marker => marker.setMap(null));
    };
  }, []);

  if (!apiKey) {
    return (
      <div className={`zipcode-map-view ${className}`.trim()}>
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3" aria-hidden="true"></i>
            <h5 className="text-warning">Google Maps API Key Required</h5>
            <p className="text-muted mb-3">
              Google Maps API key is required to display the zipcode map visualization.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={`zipcode-map-view ${className}`.trim()}>
        <div className="card">
          <div className="card-body text-center py-5">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading zipcode map...</span>
            </div>
            <p className="text-muted mb-0">Loading zipcode locations...</p>
          </div>
        </div>
      </div>
    );
  }

  const zipcodesWithCoords = data.filter(zipcode => 
    zipcode.coordinates && 
    zipcode.coordinates.lat && 
    zipcode.coordinates.lng &&
    getUserValue(zipcode) > 0
  );

  return (
    <div className={`zipcode-map-view ${className}`.trim()}>
      <div className="card">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="fas fa-map-pin me-2" aria-hidden="true"></i>
              Zip Codes - {userType === 'clients' ? 'Client' : 
                           userType === 'professionals' ? 'Professional' : 'User'} Distribution
            </h5>
            <div className="d-flex gap-2 align-items-center">
              <small className="text-muted">
                <i className="fab fa-google me-1" aria-hidden="true"></i>
                Powered by Google Maps
              </small>
              <small className="text-muted">
                Zip codes with coordinates: {zipcodesWithCoords.length}/{data.length}
              </small>
            </div>
          </div>
        </div>
        <div className="card-body p-0 position-relative">
          <div ref={mapContainerRef} style={{ height: '500px', width: '100%' }} />
          
          {/* Legend */}
          <div className="position-absolute bg-white border rounded shadow-sm p-3" style={{
            top: '10px',
            right: '10px',
            zIndex: 1,
            maxWidth: '200px'
          }}>
            <h6 className="mb-3 fw-semibold">
              {userType === 'clients' ? 'Client' : 
               userType === 'professionals' ? 'Professional' : 'User'} Count
            </h6>
            <div className="d-flex flex-column gap-2">
              <div className="d-flex align-items-center gap-2">
                <div
                  style={{
                    width: "12px",
                    height: "12px",
                    backgroundColor: "#4CAF50",
                    borderRadius: "50%",
                    border: "1px solid #fff"
                  }}
                />
                <small className="text-muted">1-4</small>
              </div>
              <div className="d-flex align-items-center gap-2">
                <div
                  style={{
                    width: "14px",
                    height: "14px",
                    backgroundColor: "#2196F3",
                    borderRadius: "50%",
                    border: "1px solid #fff"
                  }}
                />
                <small className="text-muted">5-14</small>
              </div>
              <div className="d-flex align-items-center gap-2">
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    backgroundColor: "#FF9800",
                    borderRadius: "50%",
                    border: "1px solid #fff"
                  }}
                />
                <small className="text-muted">15-29</small>
              </div>
              <div className="d-flex align-items-center gap-2">
                <div
                  style={{
                    width: "18px",
                    height: "18px",
                    backgroundColor: "#F44336",
                    borderRadius: "50%",
                    border: "1px solid #fff"
                  }}
                />
                <small className="text-muted">30-49</small>
              </div>
              <div className="d-flex align-items-center gap-2">
                <div
                  style={{
                    width: "20px",
                    height: "20px",
                    backgroundColor: "#9C27B0",
                    borderRadius: "50%",
                    border: "1px solid #fff"
                  }}
                />
                <small className="text-muted">50+</small>
              </div>
            </div>
          </div>
        </div>
        <div className="card-footer">
          <div className="row">
            <div className="col-md-6">
              <small className="text-muted">
                <i className="fas fa-mouse-pointer me-1" aria-hidden="true"></i>
                Click markers for details
              </small>
            </div>
            <div className="col-md-6 text-end">
              <small className="text-muted">
                <i className="fas fa-chart-bar me-1" aria-hidden="true"></i>
                Total {userType === 'all' ? 'users' : userType}: {data.reduce((sum, zipcode) => {
                  switch (userType) {
                    case 'clients': return sum + zipcode.client_count;
                    case 'professionals': return sum + zipcode.professional_count;
                    case 'all':
                    default: return sum + zipcode.user_count;
                  }
                }, 0).toLocaleString()}
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZipcodeMapView;
export { ZipcodeMapView };
export type { ZipcodeMapViewProps };